import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isDevFeaturesEnabled } from "../../utils/environment";
import { useAuth } from "../../contexts/AuthContext";
import { useOnboardingStore } from "../../stores/onboardingStore";
import {
  useEntitlementEntity,
  useEntitlement,
} from "../../hooks/useEntitlements";
import { TenantDropdown } from "./TenantDropdown";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Badge,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  DashboardOutlined as DashboardIcon,
  WorkOutline as CasesIcon,
  History as AuditLogsIcon,
  BugReportOutlined as DebugIcon,
  ExpandLess,
  ExpandMore,
  GppGoodOutlined as HealthcareIcon,
  CloudUploadOutlined as UploadIcon,
  FolderOutlined as FolderIcon,
  ViewListOutlined as ProcessingIcon,
  RuleOutlined as RuleIcon,
  AccountTreeOutlined as NodeResponsibilityIcon,
  CheckCircleOutline as QuestionnaireIcon,
  CreditCardOutlined as SubscriptionsIcon,
  StorefrontOutlined as PlansIcon,
  ManageAccountsOutlined as ManageIcon,
  RateReviewOutlined as ReviewIcon,
  PeopleOutlined as UsersIcon,
  BusinessOutlined as TenantsIcon,
  SecurityOutlined as RolesIcon,
  AssignmentOutlined as QuestionnaireManagementIcon,
  SettingsOutlined as AdminIcon,
  DescriptionOutlined as NeedsAssessmentIcon,
  DescriptionOutlined as DocumentTypesIcon,
  EditNoteOutlined as PromptsIcon,
  ArticleOutlined as ReportTemplateIcon,
  DownloadOutlined as DownloadIcon,
  AssignmentTurnedInOutlined as OnboardingIcon,
  ListAltOutlined as ViewQuestionnairesIcon,
  AddBoxOutlined as CreateQuestionnaireIcon,
  FolderOpenOutlined as OpenCasesIcon,
  DoneAllOutlined as ClosedCasesIcon,
} from "@mui/icons-material";
import type { NavigationItem, BaseComponentProps } from "../../types";

const DRAWER_WIDTH = 260;

// Navigation data for Alpha Health Dashboard
const navigationItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: DashboardIcon,
    path: "/",
  },
  {
    id: "cases",
    label: "Case Management",
    icon: CasesIcon,
    children: [
      {
        id: "cases-open",
        label: "Open Cases",
        icon: OpenCasesIcon,
        path: "/cases/open",
      },
      {
        id: "cases-closed",
        label: "Compliance Completed Cases",
        icon: ClosedCasesIcon,
        path: "/cases/closed",
      },
    ],
  },
  {
    id: "needs-assessment",
    label: "Needs Assessment",
    icon: NeedsAssessmentIcon,
    children: [
      {
        id: "needs-assessment-template",
        label: "Report Templates",
        icon: ReportTemplateIcon,
        path: "/needs-assessment/templates",
      },
      {
        id: "needs-assessment-download",
        label: "Download Assessment",
        icon: DownloadIcon,
        path: "/needs-assessment/download",
      },
    ],
  },
  {
    id: "healthcare",
    label: "Compliance Management",
    icon: HealthcareIcon,
    children: [
      {
        id: "healthcare-upload",
        label: "Document Upload",
        icon: UploadIcon,
        path: "/healthcare/upload",
      },
      {
        id: "healthcare-files",
        label: "Document Library",
        icon: FolderIcon,
        path: "/healthcare/files",
      },
      {
        id: "healthcare-processing",
        label: "Processing Queue",
        icon: ProcessingIcon,
        path: "/healthcare/processing",
      },
      {
        id: "healthcare-rules",
        label: "Compliance Rules",
        icon: RuleIcon,
        path: "/healthcare/rules",
      },
      {
        id: "healthcare-rule-assignments",
        label: "Rules Default Assignments",
        icon: NodeResponsibilityIcon,
        path: "/healthcare/rule-assignments",
      },
      {
        id: "compliance-questionnaire",
        label: "Completed Questionnaire",
        icon: QuestionnaireIcon,
        path: "/compliance/questionnaire",
      },
    ],
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    icon: SubscriptionsIcon,
    children: [
      {
        id: "subscription-plans",
        label: "Browse Plans",
        icon: PlansIcon,
        path: "/subscriptions/plans",
      },
      {
        id: "subscription-create",
        label: "Create Package",
        icon: CreateQuestionnaireIcon,
        path: "/subscriptions/create",
      },
      {
        id: "subscription-manage",
        label: "My Subscriptions",
        icon: ManageIcon,
        path: "/subscriptions/manage",
      },
      {
        id: "subscription-review",
        label: "Review Subscriptions",
        icon: ReviewIcon,
        path: "/subscriptions/review",
      },
    ],
  },
  {
    id: "user-management",
    label: "User Management",
    icon: UsersIcon,
    children: [
      {
        id: "tenants",
        label: "Tenant Management",
        icon: TenantsIcon,
        path: "/admin/tenants",
      },
      {
        id: "users",
        label: "Users",
        icon: UsersIcon,
        path: "/users",
      },
      {
        id: "roles",
        label: "Role Management",
        icon: RolesIcon,
        path: "/roles",
      },
    ],
  },
  {
    id: "questionnaire-management",
    label: "Questionnaire Management",
    icon: QuestionnaireManagementIcon,
    children: [
      {
        id: "questionnaires-list",
        label: "View Questionnaires",
        icon: ViewQuestionnairesIcon,
        path: "/questionnaires",
      },
      {
        id: "questionnaires-create",
        label: "Create Questionnaire",
        icon: CreateQuestionnaireIcon,
        path: "/questionnaires/new",
      },
    ],
  },
  {
    id: "admin",
    label: "System Administration",
    icon: AdminIcon,
    children: [
      {
        id: "admin-document-types",
        label: "Document Types",
        icon: DocumentTypesIcon,
        path: "/admin/document-types",
      },
      {
        id: "admin-prompts",
        label: "Extraction Prompts",
        icon: PromptsIcon,
        path: "/admin/prompts",
      },
    ],
  },
  {
    id: "audit-logs",
    label: "Audit Logs",
    icon: AuditLogsIcon,
    path: "/audit-logs",
  },
  {
    id: "debug",
    label: "API Debug",
    icon: DebugIcon,
    path: "/debug",
  },
];

// Section layout for grouped navigation
const SECTION_LAYOUT: Array<{ label?: string; itemIds: string[] }> = [
  { itemIds: ["onboarding", "dashboard"] },
  { label: "CASE MANAGEMENT", itemIds: ["cases"] },
  { label: "ASSESSMENT", itemIds: ["needs-assessment"] },
  { label: "COMPLIANCE", itemIds: ["healthcare"] },
  { label: "SUBSCRIPTIONS", itemIds: ["subscriptions"] },
  {
    label: "ADMINISTRATION",
    itemIds: ["user-management", "questionnaire-management", "admin"],
  },
  { itemIds: ["audit-logs", "debug"] },
];

interface SidebarProps extends BaseComponentProps {
  open: boolean;
  onClose: () => void;
  variant?: "permanent" | "persistent" | "temporary";
}

interface NavigationItemComponentProps {
  item: NavigationItem;
  level?: number;
  selectedPath?: string;
  onNavigate?: (path: string) => void;
}

const NavigationItemComponent: React.FC<NavigationItemComponentProps> = ({
  item,
  level = 0,
  selectedPath,
  onNavigate,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const isSelected = selectedPath === item.path;
  const isChildActive =
    hasChildren && item.children!.some((child) => selectedPath === child.path);
  const [open, setOpen] = useState(!!isChildActive);
  const Icon = item.icon;

  // Auto-expand when a child page is active
  useEffect(() => {
    if (isChildActive) {
      setOpen(true);
    }
  }, [isChildActive]);

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    } else if (item.path && onNavigate) {
      onNavigate(item.path);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          pl: level === 0 ? 2 : 3.5,
          pr: 1.5,
          py: 0.75,
          mx: 1,
          my: 0.25,
          borderRadius: 1,
          minHeight: 36,
          transition: "background-color 0.15s ease",
          backgroundColor: "transparent",
          borderLeft: "3px solid transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&.Mui-selected": {
            backgroundColor:
              level === 0
                ? "rgba(99, 102, 241, 0.08)"
                : "rgba(99, 102, 241, 0.06)",
            borderLeft:
              level === 0 ? "3px solid #6366F1" : "3px solid transparent",
            "&:hover": {
              backgroundColor: "rgba(99, 102, 241, 0.12)",
            },
            "& .MuiListItemIcon-root": {
              color: "#6366F1",
            },
            "& .MuiListItemText-primary": {
              color: "#6366F1",
              fontWeight: 600,
            },
          },
        }}
        disabled={item.disabled}
        aria-expanded={hasChildren ? open : undefined}
        aria-label={`${item.label}${hasChildren ? ", expandable" : ""}${item.badge ? `, ${item.badge} items` : ""}`}
        role={hasChildren ? "button" : "menuitem"}
      >
        {Icon ? (
          <ListItemIcon
            sx={{
              minWidth: level === 0 ? 32 : 28,
              color: isSelected ? "#6366F1" : "text.secondary",
              "& .MuiSvgIcon-root": {
                fontSize: level === 0 ? "1.125rem" : "1rem",
              },
            }}
          >
            {item.badge ? (
              <Badge badgeContent={item.badge} color="error">
                <Icon />
              </Badge>
            ) : (
              <Icon />
            )}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: level === 0 ? "0.875rem" : "0.8125rem",
            fontWeight: isSelected ? 600 : level === 0 ? 500 : 400,
            color: isSelected
              ? "#6366F1"
              : level === 0
                ? "text.primary"
                : "text.secondary",
            lineHeight: 1.5,
            noWrap: true,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
        {hasChildren && (
          <Box
            sx={{
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              opacity: 0.6,
            }}
          >
            {open ? (
              <ExpandLess sx={{ fontSize: "1.125rem" }} />
            ) : (
              <ExpandMore sx={{ fontSize: "1.125rem" }} />
            )}
          </Box>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map((child) => (
              <NavigationItemComponent
                key={child.id}
                item={child}
                level={level + 1}
                selectedPath={selectedPath}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  variant = "permanent",
  className,
  "data-testid": dataTestId,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPath, setSelectedPath] = useState(location.pathname);
  const { user, isAdmin } = useAuth();
  const { status: onboardingStatus, loadStatus } = useOnboardingStore();

  // Entitlement checks for navigation items
  const hasCaseAccess = useEntitlementEntity("Case");
  const hasDocumentAccess = useEntitlementEntity("Document");
  const hasUserAccess = useEntitlementEntity("User");
  const hasTenantAccess = useEntitlementEntity("Tenant");
  const hasPromptAccess = useEntitlementEntity("Prompt");
  const hasDocumentTypeAccess = useEntitlementEntity("DocumentType");
  const hasRoleAccess = useEntitlementEntity("Role");
  const hasRuleAccess = useEntitlementEntity("Rule");
  const hasRuleCategoryAccess = useEntitlementEntity("RuleCategory");
  const hasJobAccess = useEntitlementEntity("Job");
  const hasQuestionnaireAccess = useEntitlementEntity("Questionnaire");
  const hasAssessmentAccess = useEntitlementEntity("Assessment");
  const hasAssessmentTemplateAccess =
    useEntitlementEntity("AssessmentTemplate");
  const hasSubscriptionPackageAccess = useEntitlementEntity(
    "SubscriptionPackage",
  );
  const hasSubscriptionAccess = useEntitlementEntity("Subscription");
  const hasSubscriptionUpdateStatus = useEntitlement(
    "Subscription.UpdateStatus",
  );
  const hasAuditLogAccess = useEntitlement("Audit.Read");

  // User management menu is visible if user has User, Tenant, or Role access
  const hasUserManagementAccess =
    hasUserAccess || hasTenantAccess || hasRoleAccess;
  // Admin menu is visible if user has access to any admin feature
  const hasAdminAccess = hasPromptAccess || hasDocumentTypeAccess;
  // Compliance Management menu is visible if user has access to any compliance feature
  const hasComplianceAccess =
    hasDocumentAccess ||
    hasRuleAccess ||
    hasRuleCategoryAccess ||
    hasJobAccess ||
    hasQuestionnaireAccess;
  // Subscriptions menu is visible if user has access to subscription packages or subscriptions
  const hasSubscriptionsMenuAccess =
    hasSubscriptionPackageAccess || hasSubscriptionAccess;

  // Load onboarding status on mount
  useEffect(() => {
    if (user?.companyId && !onboardingStatus) {
      loadStatus(user.companyId);
    }
  }, [user?.companyId, onboardingStatus, loadStatus]);

  const handleNavigate = (path: string) => {
    setSelectedPath(path);
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  // Update selected path when location changes
  React.useEffect(() => {
    setSelectedPath(location.pathname);
  }, [location.pathname]);

  // Build dynamic navigation items based on onboarding status and entitlements
  const getDynamicNavigationItems = (): NavigationItem[] => {
    const items = [...navigationItems];

    // Add onboarding menu item if tenant onboarding is not complete
    if (user && !user.tenantOnboardingCompleted) {
      items.unshift({
        id: "onboarding",
        label: "Complete Onboarding",
        icon: OnboardingIcon,
        path: "/onboarding",
        badge: "!", // Badge to indicate action required
      });
    }

    // Filter user-management children based on entitlements
    const userMgmtIndex = items.findIndex(
      (item) => item.id === "user-management",
    );
    if (userMgmtIndex !== -1 && items[userMgmtIndex].children) {
      items[userMgmtIndex] = {
        ...items[userMgmtIndex],
        children: items[userMgmtIndex].children!.filter((child) => {
          if (child.id === "tenants" && !hasTenantAccess) return false;
          if (child.id === "users" && !hasUserAccess) return false;
          if (child.id === "roles" && !hasRoleAccess) return false;
          return true;
        }),
      };
    }

    // Filter admin children based on entitlements
    const adminIndex = items.findIndex((item) => item.id === "admin");
    if (adminIndex !== -1 && items[adminIndex].children) {
      items[adminIndex] = {
        ...items[adminIndex],
        children: items[adminIndex].children!.filter((child) => {
          if (child.id === "admin-document-types" && !hasDocumentTypeAccess)
            return false;
          if (child.id === "admin-prompts" && !hasPromptAccess) return false;
          return true;
        }),
      };
    }

    // Filter healthcare/compliance children based on entitlements
    const healthcareIndex = items.findIndex((item) => item.id === "healthcare");
    if (healthcareIndex !== -1 && items[healthcareIndex].children) {
      items[healthcareIndex] = {
        ...items[healthcareIndex],
        children: items[healthcareIndex].children!.filter((child) => {
          // Document Upload, Document Library require Document access
          if (
            (child.id === "healthcare-upload" ||
              child.id === "healthcare-files") &&
            !hasDocumentAccess
          )
            return false;
          // Processing Queue requires Job access
          if (child.id === "healthcare-processing" && !hasJobAccess)
            return false;
          // Compliance Rules requires Rule or RuleCategory access
          if (
            child.id === "healthcare-rules" &&
            !hasRuleAccess &&
            !hasRuleCategoryAccess
          )
            return false;
          // Rules Default Assignments requires Rule access
          if (child.id === "healthcare-rule-assignments" && !hasRuleAccess)
            return false;
          // Completed Questionnaire requires Questionnaire access and completed onboarding
          if (
            child.id === "compliance-questionnaire" &&
            (!hasQuestionnaireAccess || !user?.tenantOnboardingCompleted)
          )
            return false;
          return true;
        }),
      };
    }

    // Filter subscriptions children based on entitlements
    const subscriptionsIndex = items.findIndex(
      (item) => item.id === "subscriptions",
    );
    if (subscriptionsIndex !== -1 && items[subscriptionsIndex].children) {
      items[subscriptionsIndex] = {
        ...items[subscriptionsIndex],
        children: items[subscriptionsIndex].children!.filter((child) => {
          // Browse Plans requires SubscriptionPackage access
          if (
            child.id === "subscription-plans" &&
            !hasSubscriptionPackageAccess
          )
            return false;
          // Create Package requires SubscriptionPackage access + admin
          if (
            child.id === "subscription-create" &&
            (!hasSubscriptionPackageAccess || !isAdmin)
          )
            return false;
          // My Subscriptions requires Subscription access
          if (child.id === "subscription-manage" && !hasSubscriptionAccess)
            return false;
          // Review Subscriptions requires Subscription.UpdateStatus permission (admin only)
          if (
            child.id === "subscription-review" &&
            !hasSubscriptionUpdateStatus
          )
            return false;
          return true;
        }),
      };
    }

    return items;
  };

  // Filter navigation items based on environment, onboarding, and entitlements
  const visibleNavigationItems = getDynamicNavigationItems().filter((item) => {
    // Hide debug menu item in production
    if (item.id === "debug" && !isDevFeaturesEnabled()) {
      return false;
    }

    // Filter by entitlements
    if (item.id === "cases" && !hasCaseAccess) {
      return false;
    }
    // Compliance Management requires access to any of: Document, Rule, RuleCategory, Job, Questionnaire
    if (item.id === "healthcare" && !hasComplianceAccess) {
      return false;
    }
    // Subscriptions requires access to SubscriptionPackage or Subscription
    if (item.id === "subscriptions" && !hasSubscriptionsMenuAccess) {
      return false;
    }
    if (item.id === "user-management" && !hasUserManagementAccess) {
      return false;
    }
    if (item.id === "admin" && !hasAdminAccess) {
      return false;
    }

    // Hide parent menu items if all children are filtered out
    if (item.children && item.children.length === 0) {
      return false;
    }
    if (item.id === "questionnaire-management" && !hasQuestionnaireAccess) {
      return false;
    }
    if (
      item.id === "needs-assessment" &&
      !hasAssessmentAccess &&
      !hasAssessmentTemplateAccess
    ) {
      return false;
    }
    if (item.id === "audit-logs" && (!isAdmin || !hasAuditLogAccess)) {
      return false;
    }

    return true;
  });

  const drawerVariant = isMobile ? "temporary" : variant;

  return (
    <Drawer
      variant={drawerVariant}
      open={open}
      onClose={onClose}
      className={className}
      data-testid={dataTestId}
      {...props}
      sx={{
        // Dynamic width: 0 when closed on desktop, full width when open or on mobile
        width: !isMobile && !open ? 0 : DRAWER_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "background.paper",
          borderRight: "none",
          boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
          // Enhanced mobile experience
          ...(isMobile && {
            width: "100vw",
            maxWidth: DRAWER_WIDTH,
          }),
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
        ...(isMobile && {
          // Improve mobile drawer behavior
          slotProps: {
            backdrop: {
              sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
            },
          },
        }),
      }}
      aria-label="Main navigation menu"
    >
      <Box sx={{ overflowY: "auto", overflowX: "hidden", height: "100%" }}>
        {/* Logo/Brand Section */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: "background.default",
            // Better mobile touch targets
            minHeight: { xs: 64, sm: 70 },
          }}
        >
          <Box
            component="img"
            src="/logo.webp"
            alt="Alpha Health Logo"
            sx={{
              height: 40,
              width: "auto",
              maxWidth: 180,
              objectFit: "contain",
              objectPosition: "left center",
            }}
          />
        </Box>

        {/* Tenant Dropdown - Only visible for admins */}
        <TenantDropdown isAdmin={isAdmin} />

        {/* Navigation Items */}
        <List
          component="nav"
          sx={{ px: 0.5, py: 1 }}
          aria-label="Navigation menu"
          role="menu"
        >
          {SECTION_LAYOUT.map((section, sectionIndex) => {
            const sectionItems = section.itemIds
              .map((id) =>
                visibleNavigationItems.find((item) => item.id === id),
              )
              .filter((item): item is NavigationItem => item != null);
            if (sectionItems.length === 0) return null;
            return (
              <React.Fragment key={sectionIndex}>
                {section.label && (
                  <Typography
                    component="li"
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      px: 2,
                      pt: sectionIndex > 1 ? 2.5 : 1,
                      pb: 0.5,
                      mx: 1,
                      userSelect: "none",
                    }}
                  >
                    {section.label}
                  </Typography>
                )}
                {sectionItems.map((item) => (
                  <NavigationItemComponent
                    key={item.id}
                    item={item}
                    selectedPath={selectedPath}
                    onNavigate={handleNavigate}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
